import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3AssessmentPageState from './useTreatmentPlanV3AssessmentPageState';

import type { AppDispatch } from '../../../../../../store';
import type {
  TreatmentPlanV3AssessmentBehaviorPayload,
  TreatmentPlanV3AssessmentPagePayload,
} from '../../../../../../store/treatmentPlanV3';
import type {
  TreatmentPlanV3AssessmentBehaviorsDependencies,
  UseTreatmentPlanV3AssessmentBehaviorsResult,
} from './types';

export default function useTreatmentPlanV3AssessmentBehaviors() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const {
    assessmentPage,
    behaviors,
    noMaladaptiveBehavior,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
  } = useTreatmentPlanV3AssessmentPageState();

  return useTreatmentPlanV3AssessmentBehaviorsWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    assessmentPage,
    behaviors,
    noMaladaptiveBehavior,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
  });
}

export function useTreatmentPlanV3AssessmentBehaviorsWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  assessmentPage,
  behaviors,
  noMaladaptiveBehavior,
  isLoading,
  hasLoaded,
  hasLoadError,
  isSaving,
  isBulkSaving,
}: TreatmentPlanV3AssessmentBehaviorsDependencies): UseTreatmentPlanV3AssessmentBehaviorsResult {
  const [isClearing, setIsClearing] = useState(false);

  const create = useCallback(
    async (data: TreatmentPlanV3AssessmentBehaviorPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        actions.createTreatmentPlanV3AssessmentBehavior({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.createTreatmentPlanV3AssessmentBehavior.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (behaviorId: number, data: TreatmentPlanV3AssessmentBehaviorPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3AssessmentBehavior({
          treatmentPlanId,
          behaviorId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3AssessmentBehavior.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (behaviorId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3AssessmentBehavior({
          treatmentPlanId,
          behaviorId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3AssessmentBehavior.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const clearAll = useCallback(async () => {
    if (!treatmentPlanId) return false;
    const behaviorIds = behaviors.map((row) => row.id);
    setIsClearing(true);
    try {
      for (const behaviorId of behaviorIds) {
        const result = await dispatch(
          actions.deleteTreatmentPlanV3AssessmentBehavior({
            treatmentPlanId,
            behaviorId,
            getAccessTokenSilently,
          }),
        );
        if (!actions.deleteTreatmentPlanV3AssessmentBehavior.fulfilled.match(result)) {
          return false;
        }
      }
      return true;
    } finally {
      setIsClearing(false);
    }
  }, [actions, behaviors, dispatch, getAccessTokenSilently, treatmentPlanId]);

  const updatePage = useCallback(
    async (data: TreatmentPlanV3AssessmentPagePayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3AssessmentPage({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3AssessmentPage.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const setNoMaladaptiveBehavior = useCallback(
    async (nextNoMaladaptiveBehavior: boolean) =>
      updatePage({ no_maladaptive_behavior: nextNoMaladaptiveBehavior }),
    [updatePage],
  );

  return {
    assessmentPage,
    behaviors,
    noMaladaptiveBehavior,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving: isSaving || isClearing,
    isBulkSaving: isBulkSaving || isClearing,
    create,
    update,
    remove,
    clearAll,
    updatePage,
    setNoMaladaptiveBehavior,
  };
}
